/**
 * CSV Format Detector
 * Detects whether a CSV file is in SHOPLINE or Shopify format
 * Also supports XLSX format detection via column names
 */

// Header identifiers for different platforms
const SHOPLINE_HEADER_SIGNATURE = 'spuid,skuid,Handle,';
const SHOPIFY_HEADER_SIGNATURE = 'Handle,Title,Body (HTML)';
const SHOPLINE_V1_SIGNATURE = 'Product ID (DO NOT EDIT)';

/**
 * Detect CSV format from content
 * @param {string} csvContent - Raw CSV content
 * @returns {'shopline' | 'shopify' | 'unknown'} - Detected format
 */
export function detectCSVFormat(csvContent) {
  if (!csvContent || typeof csvContent !== 'string') {
    return 'unknown';
  }

  // Get first few lines to check headers
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());

  // Check for SHOPLINE format (has spuid,skuid,Handle in header)
  const hasShoplineHeader = lines.some(line =>
    line.includes(SHOPLINE_HEADER_SIGNATURE)
  );

  if (hasShoplineHeader) {
    return 'shopline';
  }

  // Check for Shopify format (has Handle,Title,Body (HTML) in header)
  const hasShopifyHeader = lines.some(line =>
    line.includes(SHOPIFY_HEADER_SIGNATURE)
  );

  if (hasShopifyHeader) {
    return 'shopify';
  }

  // Additional heuristics for edge cases
  // Check for Shopify-specific columns
  const hasShopifyColumns = lines.some(line =>
    line.includes('Variant SKU') ||
    line.includes('Variant Price') ||
    line.includes('Image Src')
  );

  if (hasShopifyColumns) {
    return 'shopify';
  }

  // Check for SHOPLINE-specific columns
  const hasShoplineColumns = lines.some(line =>
    line.includes('spuid') ||
    line.includes('skuid') ||
    line.includes('Title*')
  );

  if (hasShoplineColumns) {
    return 'shopline';
  }

  return 'unknown';
}

/**
 * Detect XLSX platform from column names
 * @param {string[]} columnNames - Array of column names
 * @returns {'shopline-v1' | 'unknown'} - Detected platform
 */
export function detectXlsxPlatform(columnNames) {
  if (!Array.isArray(columnNames)) {
    return 'unknown';
  }

  // Check for SHOPLINE 1.0 signature
  if (columnNames.includes(SHOPLINE_V1_SIGNATURE)) {
    return 'shopline-v1';
  }

  return 'unknown';
}

/**
 * Get platform display name
 * @param {'shopline' | 'shopify' | 'shopline-v1' | 'unknown'} format - Detected format
 * @returns {string} - Display name
 */
export function getPlatformDisplayName(format) {
  const displayNames = {
    'shopify': 'Shopify',
    'shopline': 'SHOPLINE',
    'shopline-v1': 'SHOPLINE 1.0',
    'unknown': 'Unknown Platform'
  };
  return displayNames[format] || displayNames['unknown'];
}

/**
 * Get platform icon/identifier
 * @param {'shopline' | 'shopify' | 'shopline-v1' | 'unknown'} format - Detected format
 * @returns {string} - Platform identifier for UI
 */
export function getPlatformIcon(format) {
  const icons = {
    'shopify': 'shopping_bag',
    'shopline': 'store',
    'shopline-v1': 'store',
    'unknown': 'help'
  };
  return icons[format] || icons['unknown'];
}
